import { useState, useMemo, useEffect } from "react";
import { type SortingState } from "@tanstack/react-table";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type components } from "@njbsoft/types";
import { useAppStore } from "@/store";
import { useSampleResults, type SampleResultFilters } from "@/hooks/use-sample-results";
import { useFacilities, useFacility } from "@/hooks/use-facilities";
import { useParameters } from "@/hooks/use-parameters";
import { useSampleResultColumns } from "@/components/sampling/result-columns";
import {
  PageHeader,
  DataTable,
  Pagination,
  EmptyState,
  SkeletonTable,
  Button,
  Combobox,
  inputClass,
} from "@/components/ui";

export const Route = createFileRoute("/_authenticated/sample-results/")({
  component: SampleResultsPage,
});

const PAGE_SIZE = 20;

type SampleResult = components["schemas"]["SampleResult"];

function SampleResultsPage() {
  const { t } = useTranslation(["sampling", "common"]);
  const orgId = useAppStore((s) => s.activeOrgId);
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [facilityFilter, setFacilityFilter] = useState("");
  const [facilitySearch, setFacilitySearch] = useState("");
  const [parameterFilter, setParameterFilter] = useState("");
  const [parameterSearch, setParameterSearch] = useState("");
  const [entrySourceFilter, setEntrySourceFilter] = useState("");
  const [qualifierFilter, setQualifierFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sorting, setSorting] = useState<SortingState>([{ id: "analyzed_date", desc: true }]);

  const firstSort = sorting[0];
  const sortBy = firstSort?.id as
    | "analyzed_date"
    | "created_at"
    | "parameter_name"
    | "result_value"
    | undefined;
  const sortDir = firstSort ? (firstSort.desc ? ("desc" as const) : ("asc" as const)) : undefined;

  useEffect(() => {
    setPage(1);
  }, [sorting]);

  const { data: facilitiesData, isFetching: facilitiesFetching } = useFacilities(orgId, {
    limit: 20,
    status: "active",
    search: facilitySearch || undefined,
  });
  const facilities = facilitiesData?.data ?? [];
  const { data: selectedFacility } = useFacility(orgId, facilityFilter);
  const selectedFacilityLabel = facilityFilter
    ? (facilities.find((f) => f.id === facilityFilter)?.name ?? selectedFacility?.name)
    : undefined;

  const { data: parametersData, isFetching: parametersFetching } = useParameters({
    search: parameterSearch || undefined,
    limit: 20,
  });
  const parameters = parametersData?.parameters ?? [];
  const selectedParameterLabel = parameterFilter
    ? parameters.find((p) => p.id === parameterFilter)?.name
    : undefined;

  const filters = useMemo<SampleResultFilters>(
    () => ({
      facility_id: facilityFilter || undefined,
      parameter_id: parameterFilter || undefined,
      entry_source: (entrySourceFilter || undefined) as SampleResultFilters["entry_source"],
      result_qualifier: (qualifierFilter || undefined) as SampleResultFilters["result_qualifier"],
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      sort: sortBy,
      order: sortDir,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    }),
    [
      facilityFilter,
      parameterFilter,
      entrySourceFilter,
      qualifierFilter,
      dateFrom,
      dateTo,
      page,
      sortBy,
      sortDir,
    ],
  );

  const { data, isLoading } = useSampleResults(orgId, filters);
  const columns = useSampleResultColumns(t);

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  const handleRowClick = (row: SampleResult) => {
    void navigate({
      to: "/sample-results/$resultId",
      params: { resultId: row.id },
    });
  };

  const handleExportCsv = () => {
    if (!data?.sample_results?.length) return;
    const headers = [
      "Date",
      "Parameter",
      "Code",
      "Value",
      "Qualifier",
      "Unit",
      "NODI",
      "Entry Source",
    ];
    const csvRows = data.sample_results.map((r) =>
      [
        r.analyzed_date ?? "",
        r.parameter_name ?? "",
        r.parameter_code ?? "",
        r.result_value ?? "",
        r.result_qualifier ?? "",
        r.result_unit ?? "",
        r.nodi_code ?? "",
        r.entry_source ?? "",
      ].join(","),
    );
    const csv = [headers.join(","), ...csvRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sample-results-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-8">
      <PageHeader
        title={t("results.title")}
        actions={
          <Button variant="secondary" size="sm" onClick={handleExportCsv}>
            {t("results.exportCsv")}
          </Button>
        }
      />

      {/* Filters */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Combobox
          className="w-48 shrink-0"
          value={facilityFilter}
          onChange={(val) => {
            setFacilityFilter(val);
            setPage(1);
          }}
          onSearch={setFacilitySearch}
          loading={facilitiesFetching}
          placeholder={t("filters.facility")}
          options={facilities.map((f) => ({ value: f.id, label: f.name }))}
          selectedLabel={selectedFacilityLabel}
        />
        <Combobox
          className="w-48 shrink-0"
          value={parameterFilter}
          onChange={(val) => {
            setParameterFilter(val);
            setPage(1);
          }}
          onSearch={setParameterSearch}
          loading={parametersFetching}
          placeholder={t("results.parameter")}
          options={parameters.map((p) => ({ value: p.id, label: p.name }))}
          selectedLabel={selectedParameterLabel}
        />
        <select
          className="w-40 shrink-0 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          value={entrySourceFilter}
          onChange={(e) => {
            setEntrySourceFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">{t("results.entrySource")}</option>
          <option value="manual_field">{t("results.entrySources.manual_field")}</option>
          <option value="manual_office">{t("results.entrySources.manual_lab")}</option>
          <option value="csv_import">{t("results.entrySources.lab_import")}</option>
          <option value="api_import">{t("results.entrySources.api_sync")}</option>
        </select>
        <select
          className="w-36 shrink-0 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          value={qualifierFilter}
          onChange={(e) => {
            setQualifierFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">{t("results.qualifier")}</option>
          <option value="<">{"< Below DL"}</option>
          <option value=">">{">"} Above Range</option>
          <option value="J">J Estimated</option>
          <option value="ND">ND</option>
        </select>
        <input
          type="date"
          className={`${inputClass} !w-40 shrink-0`}
          value={dateFrom}
          onChange={(e) => {
            setDateFrom(e.target.value);
            setPage(1);
          }}
          aria-label={t("filters.dateFrom")}
        />
        <input
          type="date"
          className={`${inputClass} !w-40 shrink-0`}
          value={dateTo}
          onChange={(e) => {
            setDateTo(e.target.value);
            setPage(1);
          }}
          aria-label={t("filters.dateTo")}
        />
      </div>

      {/* Data Table */}
      <div className="mt-6">
        {isLoading ? (
          <SkeletonTable columns={6} />
        ) : !data?.sample_results?.length ? (
          <EmptyState title={t("results.listEmpty")} description={t("results.listEmptyAction")} />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={data.sample_results}
              onRowClick={handleRowClick}
              sorting={sorting}
              onSortingChange={setSorting}
            />
            {totalPages > 1 && (
              <div className="mt-4">
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  totalItems={data.total}
                  pageSize={PAGE_SIZE}
                  onPageChange={setPage}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
